import { useMemo } from 'react';

import { type User, useAuth, useRegion } from '@spotify/confidence-core-react';

import { useCurrentUser } from '../useCurrentUser';

interface BuildFlagEvaluationContextOptions {
  user?: User;
  currentUserEmail?: string;
  orgName?: string;
  identity?: string;
  account?: string;
  accountCreatedAt?: string;
}

const buildFlagEvaluationContext = ({
  user,
  currentUserEmail,
  orgName,
  identity,
  account,
  accountCreatedAt,
}: BuildFlagEvaluationContextOptions): User => ({
  ...user,
  email: currentUserEmail ?? user?.email,
  org_name: orgName,
  identity,
  // Full resource name, matching what services send. Rules targeting an
  // account cannot match a browser resolve without it.
  account,
  account_created_at: accountCreatedAt,
});

export const useFlagEvaluationContext = (): User => {
  const { user, orgName, identity, isAuthenticated } = useAuth();
  const region = useRegion();
  const { currentUser, accountCreatedAt } = useCurrentUser({
    skip: !isAuthenticated || !region,
  });
  const currentUserEmail = currentUser?.user?.email;
  const account = currentUser?.account;

  return useMemo(
    () =>
      buildFlagEvaluationContext({
        user,
        currentUserEmail,
        orgName,
        identity,
        account,
        accountCreatedAt: accountCreatedAt ?? undefined,
      }),
    [user, currentUserEmail, orgName, identity, account, accountCreatedAt],
  );
};
